// src/screens/ZillowPendingAMV.tsx
import { useEffect, useState, useCallback } from 'react';
import {
  Chip,
  CircularProgress,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { apiFetch } from '../helpers';

interface ZillowProperty {
  _id: string;
  fullAddress: string;
  state?: string | null;
  price?: number | null;
  zestimate?: number | null;
  amv?: number | null;
  beds?: number | null;
  baths?: number | null;
  sqft?: number | null;
  amvStatus?: 'pending' | 'complete' | 'failed' | null;
  amvSource?: 'zestimate' | 'bofa' | 'redfin' | 'realtor' | null;
  amvError?: string | null;
  bofaFetchedAt?: string | null;
  scrapedAt?: string;
  updatedAt?: string;
  detailUrl?: string;
  isDeal?: boolean;
  dealRatio?: number | null;
  redfinActive?: boolean | null;
  realtorActive?: boolean | null;
  listingActive?: boolean | null;
  realtorPropStatus?: string[] | null;
}

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3015';
const currency = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt = (n?: number | null) => (typeof n === 'number' && n > 0 ? currency.format(n) : '—');

type ViewMode = 'all' | 'pending_amv' | 'with_zestimate' | 'with_bofa' | 'redfin_queue' | 'no_amv' | 'not_active' | 'active_check_unknown';

export default function ZillowPendingAMV() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [properties, setProperties] = useState<ZillowProperty[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [states, setStates] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>('all');

  // Scraper status
  const [scraperRunning, setScraperRunning] = useState(false);
  const [scraperLoading, setScraperLoading] = useState(false);
  const [currentCity, setCurrentCity] = useState<string | null>(null);

  // BofA AMV processing status
  const [amvProcessing, setAmvProcessing] = useState(false);
  const [amvProcessedCount, setAmvProcessedCount] = useState(0);
  const [amvFailedCount, setAmvFailedCount] = useState(0);
  const [amvLoading, setAmvLoading] = useState(false);

  // Mapbounds scraper status
  const [mapboundsRunning, setMapboundsRunning] = useState(false);
  const [mapboundsLoading, setMapboundsLoading] = useState(false);
  const [mapboundsState, setMapboundsState] = useState<string | null>(null);
  const [mapboundsStats, setMapboundsStats] = useState<{
    totalApiCalls?: number;
    totalNewAddresses?: number;
    totalFiltered?: number;
    totalDeals?: number;
  }>({});

  // Active Check service status
  const [activeCheckRunning, setActiveCheckRunning] = useState(false);
  const [activeCheckStatus, setActiveCheckStatus] = useState('');
  const [activeCheckProcessed, setActiveCheckProcessed] = useState(0);
  const [activeCheckSuccess, setActiveCheckSuccess] = useState(0);
  const [activeCheckFailed, setActiveCheckFailed] = useState(0);
  const [activeCheckLoading, setActiveCheckLoading] = useState(false);


  const loadData = useCallback(async (silent = false) => {
    try {
      setError(null);
      if (!silent) setLoading(true);

      let endpoint = '';
      const stateParam = selectedState ? `&state=${selectedState}` : '';

      if (viewMode === 'all') {
        endpoint = `/api/zillow/properties?limit=200&sort=-scrapedAt${stateParam}`;
      } else if (viewMode === 'pending_amv') {
        endpoint = `/api/zillow/properties?limit=200&sort=-scrapedAt&viewMode=pending${stateParam}`;
      } else if (viewMode === 'with_zestimate') {
        endpoint = `/api/zillow/properties?limit=200&sort=-scrapedAt&viewMode=with_zestimate${stateParam}`;
      } else if (viewMode === 'with_bofa') {
        endpoint = `/api/zillow/properties?limit=200&sort=-bofaFetchedAt&viewMode=with_bofa${stateParam}`;
      } else if (viewMode === 'redfin_queue') {
        endpoint = `/api/zillow/properties?limit=200&sort=-scrapedAt&viewMode=redfin_queue${stateParam}`;
      } else if (viewMode === 'no_amv') {
        endpoint = `/api/zillow/properties?limit=200&sort=-scrapedAt&viewMode=no_amv${stateParam}`;
      } else if (viewMode === 'not_active') {
        endpoint = `/api/zillow/properties?limit=200&sort=-updatedAt&viewMode=not_active${stateParam}`;
      } else if (viewMode === 'active_check_unknown') {
        endpoint = `/api/zillow/properties?limit=200&sort=-updatedAt&viewMode=active_check_unknown${stateParam}`;
      } else {
        endpoint = `/api/zillow/properties?limit=200&sort=-scrapedAt${stateParam}`;
      }

      const res = await apiFetch(endpoint);
      const data = await res.json();

      setProperties(data.properties || []);
      setTotalCount(data.total || data.properties?.length || 0);

      // Get unique states from properties for the filter dropdown
      if (!selectedState && data.properties?.length > 0) {
        const uniqueStates = [...new Set(data.properties.map((p: ZillowProperty) => p.state).filter(Boolean))] as string[];
        setStates(prev => prev.length > 0 ? prev : uniqueStates.sort());
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load data');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedState, viewMode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 5 seconds (silent — no spinner)
  useEffect(() => {
    const interval = setInterval(() => {
      loadData(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Fetch scraper status
  const fetchScraperStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/zillow/continuous/status`);
      const data = await res.json();
      setScraperRunning(data.isRunning || false);
      setCurrentCity(data.currentCity || null);
    } catch {
      // Ignore errors
    }
  }, []);

  // Poll scraper status
  useEffect(() => {
    fetchScraperStatus();
    const interval = setInterval(fetchScraperStatus, 5000);
    return () => clearInterval(interval);
  }, [fetchScraperStatus]);

  // Start scraper
  const handleStart = async () => {
    setScraperLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/zillow/continuous/start`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setScraperRunning(true);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to start scraper');
    }
    setScraperLoading(false);
  };

  // Stop scraper
  const handleStop = async () => {
    setScraperLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/zillow/continuous/stop`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setScraperRunning(false);
        setCurrentCity(null);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to stop scraper');
    }
    setScraperLoading(false);
  };

  // Fetch mapbounds scraper status
  const fetchMapboundsStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/zillow/mapbounds/status`);
      const data = await res.json();
      setMapboundsRunning(data.isRunning || false);
      setMapboundsState(data.currentState || null);
      if (data.stats) setMapboundsStats(data.stats);
    } catch {
      // Ignore errors
    }
  }, []);

  // Poll mapbounds status
  useEffect(() => {
    fetchMapboundsStatus();
    const interval = setInterval(fetchMapboundsStatus, 5000);
    return () => clearInterval(interval);
  }, [fetchMapboundsStatus]);

  // Start mapbounds scraper
  const handleStartMapbounds = async () => {
    setMapboundsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/zillow/mapbounds/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success) {
        setMapboundsRunning(true);
        fetchMapboundsStatus();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to start mapbounds scraper');
    }
    setMapboundsLoading(false);
  };

  // Stop mapbounds scraper
  const handleStopMapbounds = async () => {
    setMapboundsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/zillow/mapbounds/stop`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setMapboundsRunning(false);
        setMapboundsState(null);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to stop mapbounds scraper');
    }
    setMapboundsLoading(false);
  };

  // Fetch BofA AMV status
  const fetchAmvStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/zillow/bofa-status`);
      const data = await res.json();
      setAmvProcessing(data.isProcessing || false);
      setAmvProcessedCount(data.processedCount || 0);
      setAmvFailedCount(data.failedCount || 0);
    } catch {
      // Ignore errors
    }
  }, []);

  // Poll AMV status
  useEffect(() => {
    fetchAmvStatus();
    const interval = setInterval(fetchAmvStatus, 3000);
    return () => clearInterval(interval);
  }, [fetchAmvStatus]);

  // Start AMV processing for selected state (or all states if none selected)
  const handleStartAmv = async () => {
    setAmvLoading(true);
    try {
      const stateToProcess = selectedState || (states.length > 0 ? states[0] : 'AL');
      const res = await fetch(`${API_BASE}/api/zillow/process-amv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state: stateToProcess })
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        fetchAmvStatus();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to start AMV processing');
    }
    setAmvLoading(false);
  };

  // ---- Active Check Service ----
  const fetchActiveCheckStatus = useCallback(async () => {
    try {
      const res = await apiFetch('/api/zillow-active-check/status');
      const data = await res.json();
      setActiveCheckRunning(data.isRunning || false);
      setActiveCheckStatus(data.currentStatus || '');
      setActiveCheckProcessed(data.processedCount || 0);
      setActiveCheckSuccess(data.successCount || 0);
      setActiveCheckFailed(data.failedCount || 0);
    } catch {
      // Ignore errors
    }
  }, []);

  useEffect(() => {
    fetchActiveCheckStatus();
    const interval = setInterval(fetchActiveCheckStatus, 3000);
    return () => clearInterval(interval);
  }, [fetchActiveCheckStatus]);

  const handleStartActiveCheck = async () => {
    setActiveCheckLoading(true);
    try {
      const res = await apiFetch('/api/zillow-active-check/start', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setActiveCheckRunning(true);
        fetchActiveCheckStatus();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to start active check');
    }
    setActiveCheckLoading(false);
  };

  const handleStopActiveCheck = async () => {
    setActiveCheckLoading(true);
    try {
      const res = await apiFetch('/api/zillow-active-check/stop', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setActiveCheckRunning(false);
        fetchActiveCheckStatus();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to stop active check');
    }
    setActiveCheckLoading(false);
  };

  const getTitle = () => {
    if (viewMode === 'all') return 'All Properties';
    if (viewMode === 'pending_amv') return 'Pending AMV Check';
    if (viewMode === 'with_zestimate') return 'With Zestimate';
    if (viewMode === 'with_bofa') return 'With BofA AMV';
    if (viewMode === 'redfin_queue') return 'Redfin Queue';
    if (viewMode === 'no_amv') return 'No AMV Found';
    if (viewMode === 'not_active') return 'Not Active';
    if (viewMode === 'active_check_unknown') return 'Active Check Unknown';
    return 'All Properties';
  };

  const isActiveCheckTab = viewMode === 'pending_amv' || viewMode === 'not_active' || viewMode === 'active_check_unknown';

  return (
    <div style={{ padding: 24, background: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111' }}>
            Zillow Properties
          </h2>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={loadData}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </div>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {/* View Mode Toggle */}
      <div style={{ marginBottom: 16 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 700 }}>
            All Properties
          </ToggleButton>
          <ToggleButton value="pending_amv" sx={{ textTransform: 'none', fontWeight: 600, color: viewMode === 'pending_amv' ? '#1d4ed8' : undefined }}>
            Pending AMV Check
          </ToggleButton>
          <ToggleButton value="with_zestimate" sx={{ textTransform: 'none', fontWeight: 600, color: viewMode === 'with_zestimate' ? '#166534' : undefined }}>
            With Zestimate
          </ToggleButton>
          <ToggleButton value="with_bofa" sx={{ textTransform: 'none', fontWeight: 600, color: viewMode === 'with_bofa' ? '#fff' : '#7c3aed', '&.Mui-selected': { bgcolor: '#7c3aed', color: '#fff', '&:hover': { bgcolor: '#6d28d9' } } }}>
            With BofA AMV
          </ToggleButton>
          <ToggleButton value="redfin_queue" sx={{ textTransform: 'none', fontWeight: 600, color: viewMode === 'redfin_queue' ? '#dc2626' : undefined }}>
            Redfin Queue
          </ToggleButton>
          <ToggleButton value="no_amv" sx={{ textTransform: 'none', fontWeight: 600, color: viewMode === 'no_amv' ? '#b45309' : undefined }}>
            No AMV Found
          </ToggleButton>
          <ToggleButton value="not_active" sx={{ textTransform: 'none', fontWeight: 600, color: viewMode === 'not_active' ? '#dc2626' : undefined }}>
            Not Active
          </ToggleButton>
          <ToggleButton value="active_check_unknown" sx={{ textTransform: 'none', fontWeight: 600, color: viewMode === 'active_check_unknown' ? '#9333ea' : undefined }}>
            Active Check Unknown
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 16, color: '#dc2626' }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress />
        </div>
      )}

      {/* Properties Table */}
      {!loading && (
        <div
          style={{
            overflowX: 'auto',
            borderRadius: 12,
            border: '1px solid #e5e7eb',
            background: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, color: '#111' }}>
              {getTitle()} ({totalCount})
            </span>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Filter State</InputLabel>
              <Select
                value={selectedState}
                label="Filter State"
                onChange={(e) => setSelectedState(e.target.value)}
              >
                <MenuItem value="">All States</MenuItem>
                {states.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ background: '#111827', color: '#fff' }}>
                {isActiveCheckTab ? (
                  // Active check tabs: show active check specific columns
                  ['Address', 'State', 'Price', 'Zestimate', 'Redfin', 'Realtor', 'Status', 'Updated'].map((h, i) => (
                    <th
                      key={h}
                      style={{
                        textAlign: i === 0 ? 'left' : 'right',
                        padding: '12px 14px',
                        fontSize: 12,
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))
                ) : (
                  // Other tabs: original columns
                  ['Address', 'State', 'Price', (viewMode === 'with_bofa' ? 'BofA AMV' : 'Zestimate'), 'Beds/Baths', 'Sqft', viewMode === 'with_bofa' ? 'Status' : 'Deal?', viewMode === 'with_bofa' ? 'BofA Fetched' : 'Scraped At'].map((h, i) => (
                    <th
                      key={h}
                      style={{
                        textAlign: i === 0 ? 'left' : 'right',
                        padding: '12px 14px',
                        fontSize: 12,
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {properties.map((prop, i) => {
                const zebra = i % 2 === 0 ? '#ffffff' : '#f9fafb';

                if (isActiveCheckTab) {
                  // Active check tab rows
                  return (
                    <tr key={prop._id} style={{ background: zebra }}>
                      <td style={{ padding: 14, minWidth: 280 }}>
                        <div style={{ fontWeight: 600, color: '#111' }}>
                          {prop.detailUrl ? (
                            <a href={prop.detailUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>
                              {prop.fullAddress || '—'}
                            </a>
                          ) : (
                            prop.fullAddress || '—'
                          )}
                        </div>
                      </td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>{prop.state || '—'}</td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(prop.price)}</td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#7c3aed', fontWeight: 600 }}>{fmt(prop.zestimate)}</td>
                      <td style={{ padding: 14, textAlign: 'right' }}>
                        {prop.redfinActive === true ? (
                          <Chip label="Active" size="small" sx={{ backgroundColor: '#dcfce7', color: '#166534', fontWeight: 600 }} />
                        ) : prop.redfinActive === false ? (
                          <Chip label="No" size="small" sx={{ backgroundColor: '#fef2f2', color: '#dc2626', fontWeight: 600 }} />
                        ) : (
                          <Chip label="—" size="small" sx={{ backgroundColor: '#f3f4f6', color: '#6b7280' }} />
                        )}
                      </td>
                      <td style={{ padding: 14, textAlign: 'right' }}>
                        {prop.realtorActive === true ? (
                          <Chip label="Active" size="small" sx={{ backgroundColor: '#dcfce7', color: '#166534', fontWeight: 600 }} />
                        ) : prop.realtorActive === false ? (
                          <Chip label="No" size="small" sx={{ backgroundColor: '#fef2f2', color: '#dc2626', fontWeight: 600 }} />
                        ) : (
                          <Chip label="—" size="small" sx={{ backgroundColor: '#f3f4f6', color: '#6b7280' }} />
                        )}
                      </td>
                      <td style={{ padding: 14, textAlign: 'right' }}>
                        {prop.listingActive === true ? (
                          <Chip label="ACTIVE" size="small" sx={{ backgroundColor: '#dcfce7', color: '#166534', fontWeight: 700 }} />
                        ) : prop.listingActive === false ? (
                          <Chip label="NOT ACTIVE" size="small" sx={{ backgroundColor: '#fef2f2', color: '#dc2626', fontWeight: 700 }} />
                        ) : (
                          <Chip label="Pending" size="small" sx={{ backgroundColor: '#fef3c7', color: '#b45309', fontWeight: 600 }} />
                        )}
                      </td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#6b7280', fontSize: 13 }}>
                        {prop.updatedAt ? new Date(prop.updatedAt).toLocaleString() : prop.scrapedAt ? new Date(prop.scrapedAt).toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                }

                // Original rows for other tabs
                return (
                  <tr key={prop._id} style={{ background: zebra }}>
                    <td style={{ padding: 14, minWidth: 280 }}>
                      <div style={{ fontWeight: 600, color: '#111' }}>
                        {prop.detailUrl ? (
                          <a href={prop.detailUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>
                            {prop.fullAddress || '—'}
                          </a>
                        ) : (
                          prop.fullAddress || '—'
                        )}
                      </div>
                    </td>
                    <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>{prop.state || '—'}</td>
                    <td style={{ padding: 14, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(prop.price)}</td>
                    <td style={{ padding: 14, textAlign: 'right', color: '#7c3aed', fontWeight: 600 }}>
                      {viewMode === 'with_bofa' ? fmt(prop.amv) : fmt(prop.zestimate)}
                    </td>
                    <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>
                      {prop.beds || '—'} / {prop.baths || '—'}
                    </td>
                    <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>
                      {prop.sqft ? prop.sqft.toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: 14, textAlign: 'right' }}>
                      {viewMode === 'with_bofa' ? (
                        <Chip
                          label={prop.amvSource === 'bofa' ? `AMV $${prop.amv?.toLocaleString()}` : prop.amvError || 'Failed'}
                          size="small"
                          title={prop.amvError || undefined}
                          sx={{
                            backgroundColor: prop.amvSource === 'bofa' ? '#dcfce7' : '#fef2f2',
                            color: prop.amvSource === 'bofa' ? '#166534' : '#dc2626',
                            fontWeight: 600,
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        />
                      ) : (
                        <Chip
                          label={prop.isDeal ? `Deal ${prop.dealRatio ? `(${prop.dealRatio}x)` : ''}` : 'No'}
                          size="small"
                          sx={{
                            backgroundColor: prop.isDeal ? '#dcfce7' : '#f3f4f6',
                            color: prop.isDeal ? '#166534' : '#6b7280',
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </td>
                    <td style={{ padding: 14, textAlign: 'right', color: '#6b7280', fontSize: 13 }}>
                      {viewMode === 'with_bofa'
                        ? (prop.bofaFetchedAt ? new Date(prop.bofaFetchedAt).toLocaleString() : '—')
                        : (prop.scrapedAt ? new Date(prop.scrapedAt).toLocaleString() : '—')}
                    </td>
                  </tr>
                );
              })}
              {properties.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                    {viewMode === 'pending_amv'
                      ? 'No properties pending AMV check. All caught up!'
                      : viewMode === 'with_zestimate'
                      ? 'No properties with zestimate found.'
                      : viewMode === 'with_bofa'
                      ? 'No properties with BofA AMV found.'
                      : viewMode === 'no_amv'
                      ? 'No properties without AMV found.'
                      : viewMode === 'not_active'
                      ? 'No inactive listings found.'
                      : viewMode === 'active_check_unknown'
                      ? "No properties where active check couldn't determine status."
                      : 'No Zillow properties found. Run the scraper to fetch properties.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
